import React, { useState } from "react";
import { Text, Tooltip } from "@fragments";
import {
  IconBrandInstagram,
  IconBrandTiktok,
  IconBrandYoutube,
  IconWorld,
} from "@tabler/icons-react";
import {
  citable,
  faviconUrl,
  hostName,
  officialSource,
  safeUrl,
  siteName,
} from "../../data/sources";
import "./styles.scss";

const PLATFORMS = {
  instagram: { name: "Instagram", Icon: IconBrandInstagram },
  tiktok: { name: "TikTok", Icon: IconBrandTiktok },
  youtube: { name: "YouTube", Icon: IconBrandYoutube },
};
const MAX_CHIPS = 3;

function Favicon({ url }) {
  const [failed, setFailed] = useState(false);
  if (failed)
    return (
      <IconWorld className="studio-source-chip__icon" aria-hidden="true" />
    );
  return (
    <img
      className="studio-source-chip__icon"
      src={faviconUrl(url)}
      alt=""
      width={16}
      height={16}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

// One evidence as a chip: the site's favicon and name, linking to the page, whose
// title and address show on hover. Platform aggregates have no page, only counts.
export function SourceChip({ evidence, t }) {
  if (evidence.kind === "platform_aggregate") {
    const { name, Icon } = PLATFORMS[evidence.platform] || {
      name: evidence.platform,
      Icon: IconWorld,
    };
    return (
      <span className="studio-source-chip">
        <Icon className="studio-source-chip__icon" aria-hidden="true" />
        <Text.span className="studio-source-chip__label">
          {t("sourcePlatform", {
            platform: name,
            count: evidence.aggregate?.post_count,
          })}
        </Text.span>
      </span>
    );
  }
  const url = safeUrl(evidence.url);
  const site = siteName(evidence, url);
  return (
    <Tooltip content={`${evidence.title}\n${hostName(url)}`}>
      <a
        className="studio-source-chip"
        href={url.href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t("sourceLink", { site, title: evidence.title })}
      >
        <Favicon url={url} />
        <Text.span className="studio-source-chip__label">{site}</Text.span>
      </a>
    </Tooltip>
  );
}

// The chips that close a sentence: the first sources, then how many more there are.
export function SourceChips({ evidence, t }) {
  const list = citable(evidence);
  if (!list.length) return null;
  const rest = list.slice(MAX_CHIPS);
  return (
    <span className="studio-source-chips">
      {list.slice(0, MAX_CHIPS).map((item) => (
        <SourceChip key={item.id} evidence={item} t={t} />
      ))}
      {rest.length > 0 && (
        <Tooltip
          content={rest
            .map((item) =>
              item.kind === "source_document"
                ? siteName(item)
                : PLATFORMS[item.platform]?.name || item.platform,
            )
            .join(" · ")}
        >
          <span
            className="studio-source-chip studio-source-chip--more"
            aria-label={t("sourcesMore", { count: rest.length })}
          >
            <Text.span className="studio-source-chip__label">
              +{rest.length}
            </Text.span>
          </span>
        </Tooltip>
      )}
    </span>
  );
}

// "Fonte oficial: {page title}", the source a reader should open first. The whole
// line stays muted and lifts together on hover or keyboard focus.
export function OfficialSource({ evidence, t }) {
  const [lit, setLit] = useState(false);
  const source = officialSource(evidence);
  if (!source) return null;
  const contrast = lit ? 8 : 4;
  return (
    <div
      className={`studio-official-source ${lit ? "is-lit" : ""}`}
      onMouseEnter={() => setLit(true)}
      onMouseLeave={() => setLit(false)}
      onFocus={() => setLit(true)}
      onBlur={() => setLit(false)}
    >
      <Text.span contrast={contrast}>{t("officialSource")}</Text.span>{" "}
      <a
        href={safeUrl(source.url).href}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Text.span contrast={contrast}>{source.title}</Text.span>
      </a>
    </div>
  );
}
